/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { Consts, I18nService, UNPLACEHOLDER } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type Quota } from 'app/common/types/mo';
import { ProductConfig } from 'app/config';

import { type UserOrGroup, DomainTypeE, NameTypeE, QuotaTypeE, SelectTypeE, UserOrGroupTypeE, UserOsTypeE } from './user-quota';

// 回滚速率
export enum QueryQuotaTypeE {
    All = 0,  // 所有
    Custom = 1,   // 配置
    Report = 2   // 报告
}

@Injectable({ providedIn: 'root' })
export class UserQuotaStrService {
    get = this.i18nService.get;

    percentUnit = this.get('common.common_unit_lab', ['%']);

    i18n = {
        idLab: this.get('common.ID_column'),
        dtreeLab: this.get('service.quota_term_create_Dtree_lab'),
        quotaTypeLab: this.get('service.quota_term_quotaType_lab'),
        userOrGroupLab: this.get('service.quota_term_userorGroup_lab'),
        userOrGroupTypeLab: this.get('service.quota_term_userorGroupType_lab'),
        spaceHardQuotaLab: this.get('service.quota_term_create_spaceHardQuota_column'),
        spaceSoftQuotaLab: this.get('service.quota_term_create_spaceSoftQuota_column'),
        spaceQuotaUsedLab: this.get('service.quota_term_create_spaceUsedQuota_column'),
        spaceQuotaUsedPercentLab: this.get('service.quota_term_create_spaceQuotaUsage_column') + this.percentUnit,
        fileHardQuotaLab: this.get('service.quota_term_create_fileNumberHardQuota_column'),
        fileSoftQuotaLab: this.get('service.quota_term_create_fileNumberSoftQuota_column'),
        fileQuotaUsedLab: this.get('service.quota_term_create_fileNumberUsedQuota_column'),
        fileQuotaUsedPercentLab: this.get('service.quota_term_create_fileNumberQuotaUsage_column') + this.percentUnit,
        fileSystemLab: this.get('monitor.charts_term_objectType_fileSystem_label'),
        dtreeQuotaLab: this.get('service.quota_term_quotaType_directory_lab'),
        userQuotaLab: this.get('service.quota_term_quotaType_user_lab'),
        userGroupQuotaLab: this.get('service.quota_term_quotaType_userGroup_lab'),
        spaceQuotaLab: this.get('service.quota_term_create_spaceQuota_label'),
        fileQuotaLab: this.get('service.quota_term_create_fileNumberQuota_label'),
        hardQuotaLab: this.get('service.quota_term_create_hardQuota_label'),
        softQuotaLab: this.get('service.quota_term_create_softQuota_label'),
        quotaObjectLab: this.get('service.quota_term_create_object_lab'),
        fileSystemNameLab: this.get('service.quota_term_FSName_lab'),
        allDtreeLab: this.get('service.quota_term_allDtree_lab'),
        rootDtreeLab: this.get('service.quota_term_rootDtree_lab'),
        allUserLab: this.get('service.quota_term_allUsers_lab'),
        allUserGroupLab: this.get('service.quota_term_allUserGroups_lab'),
        thousandUnit: (this.i18nService.language === 'zh' ? '' : ' ') + this.get('service.quota_term_create_thousand_unit_lab'),
        userLab: this.get('service.quota_term_user_lab'),
        specificUserLab: this.get('service.quota_term_create_specificUser_checkbox'),
        userGroupLab: this.get('service.quota_term_userGroup_lab'),
        specificUserGroupLab: this.get('service.quota_term_create_specificUserGroup_checkbox'),
        nameLab: this.get('common.name_column'),
        typeLab: this.get('common.type_column'),
        operationLab: this.get('common.operation_button'),
        unixUserLab: this.get('service.accessUser_UNIXUsers_lab'),
        windowsUserLab: this.get('service.accessUser_WindowsUsers_lab'),
        userTypeLab: this.get('service.quota_term_create_UserType_lab'),
        userGroupTypeLab: this.get('service.quota_term_create_UserGroupType_lab'),
        primaryGroupLab: this.get('service.accessUser_term_mainGroup_lab'),
        vStoreLab: this.get('common.owningVstore_lab'),
        ridLab: this.get('RID'),
        // 空间配额（软/硬）
        spaceQuotaSoftHardLab: this.get('service.quota_term_view_spaceQuota_column'),
        // 文件数配额（软/硬）
        fileQuotaSoftHardLab: this.get('service.quota_term_view_fileNumberQuota_column'),
        groupType: this.get('common.type_column'),

        dtreeTip: this.get('service.quota_term_create_Dtree_des_lab'),
        dtreeQuotaTip: this.get('service.quota_term_create_quotaType_path_des_label'),
        selectedDtreeQuotaTip: this.get('service.quota_term_create_quotaType_path_des_label1'),
        userQuotaTip: this.get('service.quota_term_create_quotaType_user_des_label'),
        userGroupQuotaTip: this.get('service.quota_term_create_quotaType_usergroup_des_label'),
        spaceHardQuotaTip: this.get('service.quota_term_create_hardQuota_tip'),
        spaceSoftQuotaTip: this.get('service.quota_term_create_softQuota_tip'),
        fileHardQuotaTip: this.get('service.quota_term_create_fileNumber_hardQuota_tip'),
        fileSoftQuotaTip: this.get('service.quota_term_create_fileNumber_softQuota_tip'),
        atLeastAddOneTip: this.get('common.addOneObject_lab'),
        atLeastSelectOneTip: this.get('common.selectOneObject_lab'),
        allUserTip: this.get('service.quota_term_create_user_allUser_desc_lab'),
        specificUserTip: this.get('service.quota_term_create_user_specificUser_desc_lab'),
        specificUserInGroupTip: this.get('service.quota_term_create_user_specificUserGroup_desc_lab'),
        allUserGroupTip: this.get('service.quota_term_create_userGroup_allUser_desc_lab'),
        specificUserGroupTip: this.get('service.quota_term_create_userGroup_specificUserGroup_desc_lab'),
        multiNameTip: this.get('common.length_multipleValue_validation', [1, 128]),
        escapeMultiNameTip: this.get('common.addUser_unix_nis_nameLength_validation', [1, 128]),
        noDtreeTip: this.get('service.quota_term_create_Dtree_emptyDtree_desc_lab'),
        adMultiNameTip: this.get('service.accessUser_term_create_name_tip', [1, 256]),
        adGroupMultiNameTip: this.get('service.accessUserGroup_term_create_name_tip', [1, 256]),
        rootErrorTip: this.get('service.quota_term_create_createQuota_root_error_tip'),
        selectVstore: this.get('service.share_term_create_chooseVstore_tip'),

        createBtn: this.get('common.create_button'),
        deleteBtn: this.get('common.delete_button'),
        modifyBtn: this.get('common.modify_button'),
        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        closeBtn: this.get('common.close_button'),
        selectBtn: this.get('storage.storagePool_task_create_requiredDisk_select_button'),
        addBtn: this.get('common.add_button'),
        removeBtn: this.get('common.remove_button'),

        createTitle: this.get('service.quota_term_create_createQuota_tittle'),
        modifyTitle: this.get('service.quota_term_create_modifyQuota_tittle'),
        deleteTitle: this.get('service.quota_term_create_deleteQuota_tittle'),
        selectUserTitle: this.get('service.quota_term_create_selectUser_title'),
        selectUserGroupTitle: this.get('service.quota_term_create_selectUserGroup_title'),
        addUserTitle: this.get('service.quota_term_create_addUser_title'),
        addUserGroupTitle: this.get('service.quota_term_create_addUserGroup_title'),
        selectionTitle: this.get('common.selectedList_lab'),

        quotaRequiredErr: this.get('service.quota_term_create_setQuotaPara_info_message'),
        quotaValueRequiredErr: this.get('service.quota_term_create_setQuotaPara_info_message2'),
        quotaCompareErr: this.get('service.quota_term_create_hardQuotaLarger_info_message'),
        adUserNameFormatErr: this.get('service.share_term_createCIFS_addADUser_name_validation'),
        adUserGroupNameFormatErr: this.get('service.share_term_createCIFS_addADUserGroup_name_validation'),

        deleteMsg: this.get('service.quota_task_delete_confirm_msg'),
        adUserGrouplabel: this.get('service.quota_term_create_UserGroupType_ADdomain_value'),
    };

    spaceQuotaMin = '1 KB';
    spaceQuotaMax = '256 PB';

    fileQuotaMin = 1;
    fileQuotaMax = 2000000000;

    capacityUnits: string[] = [Consts.KB, Consts.MB, Consts.GB, Consts.TB, Consts.PB];
    fileQuotaUnits: SelectOption[] = [
        {
            label: this.get('common.one_unit'),
            value: 1,
        },
        {
            label: this.get('common.hundred_unit'),
            value: 100,
        },
        {
            label: this.get('common.thousand_unit'),
            value: 1000,
        },
        {
            label: this.get('common.million_unit'),
            value: 1000000,
        },
    ];

    /**
     * 选择用户的选项
     */
    userSelectTypeOpts: SelectOption[] = [
        {
            label: this.i18n.allUserLab,
            value: SelectTypeE.Every,
            data: this.i18n.allUserTip,
        },
        {
            label: this.i18n.specificUserLab,
            value: SelectTypeE.SpecificUserOrGroup,
            data: this.i18n.specificUserTip,
        },
        {
            label: this.i18n.specificUserGroupLab,
            value: SelectTypeE.SpecificUserInGroup,
            data: this.i18n.specificUserInGroupTip,
        },
    ];

    /**
     * 选择用户组的选项
     */
    userGroupSelectTypeOpts: SelectOption[] = [
        {
            label: this.i18n.allUserGroupLab,
            value: SelectTypeE.Every,
            data: this.i18n.allUserGroupTip,
        },
        {
            label: this.i18n.specificUserGroupLab,
            value: SelectTypeE.SpecificUserOrGroup,
            data: this.i18n.specificUserGroupTip,
        },
    ];

    /**
     * 配额类型对应的资料
     */
    quotaTypeI18n = {
        [QuotaTypeE.Dtree]: this.i18n.dtreeQuotaLab,
        [QuotaTypeE.User]: this.i18n.userQuotaLab,
        [QuotaTypeE.UserGroup]: this.i18n.userGroupQuotaLab,
    };

    /**
     * Unix用户类型
     */
    unixUserDomainTypeOpts: SelectOption[] = [
        {
            label: this.get('service.quota_term_create_userTyper_localUser_value'),
            value: DomainTypeE.Local,
        },
        {
            label: this.get('service.quota_term_create_UserType_LDAPdomain_value'),
            value: DomainTypeE.LDAP,
        },
        {
            label: this.get('service.quota_term_create_UserType_NISdomain_value'),
            value: DomainTypeE.NIS,
        },
    ];

    /**
     * Unix用户组类型
     */
    unixUserGroupDomainTypeOpts: SelectOption[] = [
        {
            label: this.get('service.quota_term_create_userTyper_localUserGroup_value'),
            value: DomainTypeE.Local,
        },
        {
            label: this.get('service.quota_term_create_UserGroupType_LDAPdomain_value'),
            value: DomainTypeE.LDAP,
        },
        {
            label: this.get('service.quota_term_create_UserGroupType_NISdomain_value'),
            value: DomainTypeE.NIS,
        },
    ];

    /**
     * windows 用户类型
     */
    windowsUserDomainTypeOpts: SelectOption[] = [
        {
            label: this.get('service.quota_term_create_userTyper_localUser_value'),
            value: DomainTypeE.Local,
        },
        {
            label: this.get('service.quota_term_create_UserType_ADdomain_value'),
            value: DomainTypeE.AD,
        },
    ];

    /**
     * windows 用户组类型
     */
    windowsUserGroupDomainTypeOpts: SelectOption[] = [
        {
            label: this.get('service.quota_term_create_UserGroupType_ADdomain_value'),
            value: DomainTypeE.AD,
        },
    ];

    /**
     * 配额类型
     */
    quotaTypeOpts = [
        {
            label: this.get('service.quota_term_quotaType_directory_lab'),
            value: QuotaTypeE.Dtree,
        },
        {
            label: this.get('service.quota_term_quotaType_user_lab'),
            value: QuotaTypeE.User,
        },
        {
            label: this.get('service.quota_term_quotaType_userGroup_lab'),
            value: QuotaTypeE.UserGroup,
        },
    ];

    /**
     * 组类型
     * 1 内置组,
     * 2 自定义组
     */
    groupTypeOpt = [
        {
            value: '1',
            label: this.get('service.accessUser_userGroupType_default_value'),
        },
        {
            value: '2',
            label: this.get('service.accessUser_userGroupType_userCreate_value'),
        },
    ];

    constructor(
        private i18nService: I18nService,
        private toolService: ToolService,
    ) { }

    /**
     * 文件数配额单位
     */
    fileQuotaRange = (min, max): string => {
        const str = this.get('common.fileNum_unit', [min, max]);
        return this.get('common.common_unit_lab', [str]);
    };

    billion = (value): string => this.get('common.billion_unit', [value]);

    /**
     * 创建结果
     * @param type 配额类型
     * @param name 配额类型对应的名称，为空则表示所有
     */
    createResult(type: QuotaTypeE, name?: string, nameType?: NameTypeE): string {
        if (type === QuotaTypeE.Dtree) {
            if (name) {
                return this.get('rest.createPathQuota_msg', [name]);
            } else {
                return this.get('rest.createAllPathQuota_msg');
            }
        } else if (type === QuotaTypeE.User) {
            if (name) {
                if (nameType === NameTypeE.Group) {
                    return this.get('rest.createUserGroupUserQuota_msg', [name]);
                }
                return this.get('rest.createUserQuota_msg', [name]);
            } else {
                return this.get('rest.createAllUserQuota_msg');
            }
        } else {
            if (name) {
                return this.get('rest.createUsergroupQuota_msg', [name]);
            } else {
                return this.get('rest.createAllUsergroupQuota_msg');
            }
        }
    }

    /**
     * 修改结果
     */
    modiyResult(data: Quota): string {
        const dtreeName = this.resUseNameRender(data.RESUSENAME, null, data);
        const userOrGroupName = this.userOrGroupRender(data.USRGRPOWNERNAME, null, data);

        if (Number(data.QUOTATYPE) === QuotaTypeE.Dtree) {
            return this.get('rest.modifyDtreeQuota_msg', [dtreeName]);
        }
        if (Number(data.QUOTATYPE) === QuotaTypeE.User) {
            if (ProductConfig.isHideDtree) {
                return this.get('rest.modifyUserQuota_msg', [userOrGroupName]);
            }
            return this.get('rest.modifyUserQuota_msg', [dtreeName, userOrGroupName]);
        }
        if (ProductConfig.isHideDtree) {
            return this.get('rest.modifyUserGroupQuota_msg', [userOrGroupName]);
        }
        return this.get('rest.modifyUserGroupQuota_msg', [dtreeName, userOrGroupName]);
    }

    /**
     * 删除结果
     */
    deleteResult(data: Quota): string {
        const dtreeName = this.resUseNameRender(data.RESUSENAME, null, data);
        const userOrGroupName = this.userOrGroupRender(data.USRGRPOWNERNAME, null, data);

        if (Number(data.QUOTATYPE) === QuotaTypeE.Dtree) {
            return this.get('rest.deleteDtreeQuota_msg', [dtreeName]);
        }
        if (Number(data.QUOTATYPE) === QuotaTypeE.User) {
            if (ProductConfig.isHideDtree) {
                return this.get('rest.deleteUserQuota_msg', [userOrGroupName]);
            }
            return this.get('rest.deleteUserQuota_msg', [dtreeName, userOrGroupName]);
        }
        if (ProductConfig.isHideDtree) {
            return this.get('rest.deleteUserGroupQuota_msg', [userOrGroupName]);
        }
        return this.get('rest.deleteUserGroupQuota_msg', [dtreeName, userOrGroupName]);
    }

    /**
     * Dtree列渲染
     * @param value
     */
    resUseNameRender = (value: string, col, data: Quota): string => {
        if (!value || this.toolService.isInvalidNum(value)) {
            if (Number(data.QUOTATYPE) !== QuotaTypeE.Dtree) {
                return UNPLACEHOLDER;
            }
            return this.i18n.allDtreeLab;
        }
        return value;
    };

    /**
     * 用户渲染
     * 如果是用户组，展示 --
     */
    userRender = (value: string, col, data: UserOrGroup | Quota): string => {
        if (Number(data.NAMETYPE) === NameTypeE.User) {
            return this.userOrGroupRender(value, col, data);
        }
        return UNPLACEHOLDER;
    };

    /**
     * 用户组
     * 如果是用户，展示 --
     */
    groupRender = (value: string, col, data: UserOrGroup | Quota): string => {
        if (Number(data.NAMETYPE) === NameTypeE.Group || Number(data.QUOTATYPE) === QuotaTypeE.UserGroup) {
            return this.userOrGroupRender(value, col, data);
        }
        return UNPLACEHOLDER;
    };

    /**
     * 用户/用户组渲染
     * windows 本地用户用户特殊处理
     */
    userOrGroupRender = (value: string, col, data: UserOrGroup | Quota): string => {
        if (value === 'All User') {
            return this.i18n.allUserLab;
        }
        if (value === 'All Group') {
            return this.i18n.allUserGroupLab;
        }
        if (Number(data.DOMAINTYPE) === DomainTypeE.Local) {
            return value.split('localhost\\').pop() || UNPLACEHOLDER;
        }
        return value || UNPLACEHOLDER;
    };

    /**
     * 获取用户/用户组类型字符串
     */
    getTypeStr = (isUser: boolean, isWindows: boolean): string => {
        if (isUser) {
            if (isWindows) {
                return this.get('service.share_term_createCIFS_addUser_userType_winLocalUser_listbox');
            }
            return this.get('service.share_term_createCIFS_addUser_userType_UNIXLocalUser_listbox');
        }
        if (isWindows) {
            return this.get('service.share_term_createCIFS_addUser_userType_winLocalUserGroup_listbox');
        }
        return this.get('service.share_term_createCIFS_addUser_userType_UNIXLocalUserGroup_listbox');
    };

    /**
     * 获取用户/用户组类型
     */
    getLocalType = (isUser: boolean, data: UserOrGroup & Quota): string => {
        const isWindows = Number(data.usrOsType) === UserOsTypeE.Windows;
        if (isUser) {
            if (isWindows) {
                return this.get('service.share_term_createCIFS_addUser_userType_winLocalUser_listbox');
            }
            return this.get('service.share_term_createCIFS_addUser_userType_UNIXLocalUser_listbox');
        }
        if (isWindows) {
            return this.get('service.share_term_createCIFS_addUser_userType_winLocalUserGroup_listbox');
        }
        return this.get('service.share_term_createCIFS_addUser_userType_UNIXLocalUserGroup_listbox');
    };


    /**
     * 用户/用户组类型渲染
     */
    userOrGroupTypeRender = (value, col, data: UserOrGroup & Quota): string => {
        let result = '';

        // 所有用户/用户组的类型显示成--
        if (data.USRGRPOWNERNAME === 'All User' || data.USRGRPOWNERNAME === 'All Group') {
            result = UNPLACEHOLDER;
        }

        if (Number(data.USRGRPTYPE) === UserOrGroupTypeE.Local) {
            if (Number(data.NAMETYPE) === NameTypeE.User) {
                result = this.get('service.quota_term_create_userTyper_localUser_value');
            } else if (Number(data.NAMETYPE) === NameTypeE.Group) {
                result = this.get('service.quota_term_create_userTyper_localUserGroup_value');
            }
        } else if (Number(data.USRGRPTYPE) === UserOrGroupTypeE.Domain) {
            if (Number(data.NAMETYPE) === NameTypeE.User) {
                if (Number(data.DOMAINTYPE) === DomainTypeE.AD) {
                    result = this.get('service.quota_term_create_UserType_ADdomain_value');
                } else if (Number(data.DOMAINTYPE) === DomainTypeE.LDAP) {
                    result = this.get('service.quota_term_create_UserType_LDAPdomain_value');
                } else if (Number(data.DOMAINTYPE) === DomainTypeE.NIS) {
                    result = this.get('service.quota_term_create_UserType_NISdomain_value');
                }
            } else if (Number(data.NAMETYPE) === NameTypeE.Group) {
                if (Number(data.DOMAINTYPE) === DomainTypeE.AD) {
                    result = this.get('service.quota_term_create_UserGroupType_ADdomain_value');
                }
                if (Number(data.DOMAINTYPE) === DomainTypeE.LDAP) {
                    result = this.get('service.quota_term_create_UserGroupType_LDAPdomain_value');
                }
                if (Number(data.DOMAINTYPE) === DomainTypeE.NIS) {
                    result = this.get('service.quota_term_create_UserGroupType_NISdomain_value');
                }
            }
        }

        // 本地域需要区分unix 和 windows
        if (Number(data.DOMAINTYPE) === DomainTypeE.Local) {
            if (Number(data.NAMETYPE) === NameTypeE.User) {
                return this.getLocalType(true, data);
            } else if (Number(data.NAMETYPE) === NameTypeE.Group) {
                return this.getLocalType(false, data);
            }
        }

        return result || UNPLACEHOLDER;
    };

    /**
     * 范围提示
     */
    rangeTip = (min: number | string, max: number | string): string => this.get('common.valueRange_label', [min, max]);

    /**
     * 已选用户描述信息
     */
    getUsersDesc(users: UserOrGroup[]): string {
        if (users === undefined) {
            return undefined as string;
        }
        if (users === null) {
            return this.i18n.allUserLab;
        }
        if (users[0].NAMETYPE === NameTypeE.Group) {
            return this.get('service.quota_term_create_userGroupQuato_desc_lab', [users.length]);
        }
        return this.get('service.quota_term_create_userQuato_desc_lab', [users.length]);
    }

    /**
     * 已选用户组描述信息
     */
    getUserGroupsDesc(userGroups: UserOrGroup[]): string {
        if (userGroups === undefined) {
            return undefined as string;
        }
        if (userGroups === null) {
            return this.i18n.allUserGroupLab;
        }
        return this.get('service.quota_term_create_userGroupQuato_desc_lab', [userGroups.length]);
    }

    rangeByteOneLengthErr(min: number, max: number): string {
        return this.get('common.length_oneValue_tip', [min, max]);
    }

    /**
     * 渲染文件数配额
     */
    randerFileQuota(fileQuotaValue: string): { value: any; unit: number } {
        let value;
        let unit = 1;
        if (!fileQuotaValue) {
            return { value, unit };
        }
        const ones = Number(fileQuotaValue);
        const hundreds = ones / 100;
        const thousands = hundreds / 10;
        const millions = thousands / 1000;
        if (millions === Math.floor(millions)) {
            value = millions;
            unit = 1000000;
        } else if (thousands === Math.floor(thousands)) {
            value = thousands;
            unit = 1000;
        } else if (hundreds === Math.floor(hundreds)) {
            value = hundreds;
            unit = 100;
        } else {
            value = ones;
            unit = 1;
        }
        return { value, unit };
    }
}
